import React from "react";

import { Form, FormMessage, Accordion, Button, FormControl } from "lib/components/index";
import CSSModules from "lib/utils/css-modules";
import st from "lib/utils/index";
import { ProductScheduleSlotDecorator, ProductScheduleDecorator } from "lib/decorators/index";
import SlotTimeFormSection from "./SlotTimeFormSection";
import { SALE_WINDOW_EDGES, saleWindowStatus } from "lib/utils/saleWindow";

// Shared with the calendar's ShowSlotModal — see StepAvailability.less
// for the sale-window / Biledo metadata classes. The class itself is
// wrapped in @CSSModules(styles) below; without that decorator `styleName`
// silently does nothing and the sale-window / Biledo blocks render
// unstyled.
import styles from "./StepAvailability.less";

const ACCORDION_SCHEDULE = 0;
const ACCORDION_PRICINGS = 1;
const ACCORDION_AVAILABILITY = 2;
const ACCORDION_NOTE = 3;
const ACCORDION_SALE_WINDOW = 4;

@CSSModules(styles)
export default class ProductScheduleSlotForm extends React.PureComponent {
  state = {
    accordionActiveIds: [],
    totalStockLimitUpdated: false,
  };

  componentDidMount() {
    const { slot } = this.props;
    const accordionActiveIds = [];
    if (slot._showAllAccordions || (slot.product_schedule || slot).price_pricings.length > 0) {
      accordionActiveIds.push(ACCORDION_PRICINGS);
    }
    if (slot._showAllAccordions || (slot.product_schedule || slot).total_stock_limit !== null) {
      accordionActiveIds.push(ACCORDION_AVAILABILITY);
    }
    if (slot._showAllAccordions || (slot.product_schedule || slot).note) {
      accordionActiveIds.push(ACCORDION_NOTE);
    }
    // Open the sale window section when a rule is already in play — either
    // pre-filled from the variant defaults or carried by an edited slot —
    // so the operator sees what will be saved without hunting for it.
    if (
      slot._showAllAccordions ||
      SALE_WINDOW_EDGES.some(({ attribute }) => slot[attribute] != null || slot[`${attribute}_offset_seconds`] != null)
    ) {
      accordionActiveIds.push(ACCORDION_SALE_WINDOW);
    }
    this.setState({ accordionActiveIds });
  }

  handleAccordionTitleClick(index) {
    const { onBlur } = this.props;
    const { accordionActiveIds } = this.state;
    this.setState({
      accordionActiveIds: accordionActiveIds.includes(index)
        ? accordionActiveIds.filter((i) => i !== index)
        : accordionActiveIds.concat([index]),
    });
  }

  handleSubmit(event) {
    event.preventDefault();
    this.props.onSubmit();
  }

  handleSchedulePropChange(prop, value) {
    const { slot, onChange } = this.props;
    const newSlot = new ProductScheduleSlotDecorator({
      ...slot,
      product_schedule: {
        ...(slot.product_schedule || this.getDefaultSchedule()),
        [prop]: value,
      },
    });
    onChange(newSlot);
  }

  handleSlotPropChange(prop, value) {
    const { slot, onChange } = this.props;
    const { totalStockLimitUpdated } = this.state;
    let newSlot = slot;
    // Set those attributes for both slot and schedule
    if (
      [
        "product_variant_id",
        "active",
        "confirmed",
        "availability_type",
        "total_stock_limit",
        "price_pricings",
        "note",
      ].includes(prop)
    ) {
      if (prop === "total_stock_limit" && !totalStockLimitUpdated) this.setState({ totalStockLimitUpdated: true });
      if (prop === "product_variant_id" && totalStockLimitUpdated) this.setState({ totalStockLimitUpdated: false });
      newSlot = new ProductScheduleSlotDecorator({
        ...slot,
        product_schedule: slot.product_schedule
          ? {
              ...slot.product_schedule,
              [prop]: value,
            }
          : undefined,
        [prop]: value,
      });
    } else {
      newSlot = new ProductScheduleSlotDecorator({
        ...slot,
        [prop]: value,
      });
    }
    onChange(newSlot);
  }

  // One edge of the sale window. The offset and the fixed datetime are
  // mutually exclusive, so both are always written together. Kept on the
  // slot (never on the schedule) — the backend derives the schedule's rule
  // from what the form sends for the edited slot.
  handleSaleWindowChange(attribute, { offset_seconds, at }) {
    const { slot, onChange } = this.props;
    onChange(
      new ProductScheduleSlotDecorator({
        ...slot,
        [`${attribute}_offset_seconds`]: offset_seconds,
        [attribute]: at,
      })
    );
  }

  handlePricingChange(pricing, nextAmount) {
    const { slot, onChange } = this.props;
    const nextPricing =
      nextAmount && nextAmount.value !== null
        ? {
            ...pricing,
            absolute_amount_cents: nextAmount.value * 100,
          }
        : null;
    const price_pricings = (slot.product_schedule || slot).price_pricings
      .filter((pr) => pr.uuid !== pricing.uuid)
      .concat(nextPricing ? [nextPricing] : []);
    return this.handleSlotPropChange("price_pricings", price_pricings);
  }

  getDefaultSchedule() {
    const { slot } = this.props;
    return new ProductScheduleDecorator({
      uuid: st.generateUuid(),
      start_dates_assignation_type: null,
      start_dates: slot.start_date ? [slot.start_date] : [],
      start_date_recursion_from: slot.start_date,
      start_date_recursion_to: slot.start_date,
      start_date_recursion_wdays: [],
      start_times_assignation_type: null,
      start_times: slot.start_time ? [slot.start_time] : [],
      start_time_recursion_from: slot.start_time,
      start_time_recursion_to: slot.start_time,
      time_duration: slot.time_duration,
      time_zone_name: slot.time_zone_name,
      product_variant_id: slot.product_variant_id,
      active: slot.active,
      confirmed: slot.confirmed,
      availability_type: slot.availability_type,
      total_stock_limit: slot.total_stock_limit,
      note: slot.note,
      price_pricings: slot.price_pricings,
      sale_starts_at_offset_seconds: slot.sale_starts_at_offset_seconds,
      sale_ends_at_offset_seconds: slot.sale_ends_at_offset_seconds,
    });
  }

  renderRecurrenceInputs() {
    const { readOnlyAccess, slot, name, errors, onBlur } = this.props;
    const schedule = slot.product_schedule || this.getDefaultSchedule();

    return (
      <div>
        <div className="ui fields inline">
          {!slot._forceStartDateRecurrence && (
            <FormControl.CheckboxFormGroup
              {...FormControl.addProps(`${name}.start_dates_assignation_type`, { errors })}
              value={schedule.guess_start_dates_assignation_type() || "recursion"}
              checked={!!schedule.start_dates_assignation_type}
              onBlur={onBlur}
              onChange={this.handleSchedulePropChange.bind(this, "start_dates_assignation_type")}
              label={I18n.t("papp.products.EditProductView.StepAvailability.repeat_on_days")}
              toggle
              readOnly={readOnlyAccess}
            />
          )}
          {slot._forceStartDateRecurrence && (
            <span className="u-marginRight">
              {I18n.t("papp.products.EditProductView.StepAvailability.repeat_on_days")}
            </span>
          )}

          {!!schedule.start_dates_assignation_type && (
            <FormControl.SelectFormGroup
              {...FormControl.addProps(`${name}.start_dates_assignation_type`, { errors })}
              value={schedule.start_dates_assignation_type}
              onBlur={onBlur}
              onChange={this.handleSchedulePropChange.bind(this, "start_dates_assignation_type")}
              options={[
                [
                  "normal",
                  I18n.t("papp.products.EditProductView.StepAvailability.normal_start_dates_assignation_type"),
                ],
                [
                  "recursion",
                  I18n.t("papp.products.EditProductView.StepAvailability.recursion_start_dates_assignation_type"),
                ],
              ]}
              required
              className="u-smallWidth"
              readOnly={readOnlyAccess}
            />
          )}
        </div>

        {schedule.start_dates_assignation_type === "normal" && (
          <FormControl.MultipleDateFormGroup
            {...FormControl.addProps(`${name}.start_dates`, { errors })}
            value={schedule.start_dates}
            onBlur={onBlur}
            onChange={this.handleSchedulePropChange.bind(this, "start_dates")}
            required
            keepOpenOnDateSelect
            readOnlyValues={[slot.start_date]}
            min={slot.start_date}
            readOnly={readOnlyAccess}
          />
        )}

        {schedule.start_dates_assignation_type === "recursion" && (
          <div>
            <div className="ui fields inline">
              <FormControl.DateFormGroup
                {...FormControl.addProps(`${name}.start_date_recursion_to`, { errors })}
                value={schedule.start_date_recursion_to}
                onBlur={onBlur}
                onChange={this.handleSchedulePropChange.bind(this, "start_date_recursion_to")}
                label={I18n.t("papp.products.EditProductView.StepAvailability.repeat_until")}
                min={schedule.start_date_recursion_from}
                initialValue={
                  schedule.start_date_recursion_to || schedule.start_date_recursion_from || slot.start_date || undefined
                }
                readOnly={readOnlyAccess}
              />
            </div>

            {
              <FormControl.MultipleCheckboxFormGroup
                {...FormControl.addProps(`${name}.start_date_recursion_wdays`, { errors })}
                value={schedule.start_date_recursion_wdays || []}
                onBlur={onBlur}
                onChange={this.handleSchedulePropChange.bind(this, "start_date_recursion_wdays")}
                options={[1, 2, 3, 4, 5, 6, 0].map((wday) => [wday, I18n.t("date.abbr_day_names")[wday]])}
                withLabelsAbove
                disabledValues={slot.start_date ? [+slot.start_date_moment.format("d")] : undefined}
                readOnly={readOnlyAccess}
              />
            }
          </div>
        )}

        {slot.includes_time && (
          <div className="field">
            <div className="ui fields inline">
              <FormControl.CheckboxFormGroup
                {...FormControl.addProps(`${name}.start_times_assignation_type`, { errors })}
                value={schedule.guess_start_times_assignation_type() || "recursion"}
                checked={!!schedule.start_times_assignation_type}
                onBlur={onBlur}
                onChange={this.handleSchedulePropChange.bind(this, "start_times_assignation_type")}
                label={I18n.t("papp.products.EditProductView.StepAvailability.repeat_in_hours")}
                toggle
                readOnly={readOnlyAccess}
              />

              {!!schedule.start_times_assignation_type && (
                <FormControl.SelectFormGroup
                  {...FormControl.addProps(`${name}.start_times_assignation_type`, { errors })}
                  value={schedule.start_times_assignation_type}
                  onBlur={onBlur}
                  onChange={this.handleSchedulePropChange.bind(this, "start_times_assignation_type")}
                  options={[
                    ["normal", "wybrane"],
                    ["recursion", "cyklicznie"],
                  ]}
                  required
                  className="u-smallWidth"
                  readOnly={readOnlyAccess}
                />
              )}
            </div>

            {schedule.start_times_assignation_type === "normal" && (
              <FormControl.MultipleTimeFormGroup
                {...FormControl.addProps(`${name}.start_times`, { errors })}
                value={schedule.start_times}
                onBlur={onBlur}
                onChange={this.handleSchedulePropChange.bind(this, "start_times")}
                readOnlyValues={[slot.start_time]}
                required
                min={slot.start_time}
                readOnly={readOnlyAccess}
              />
            )}

            {schedule.start_times_assignation_type === "recursion" && (
              <div className="ui fields inline">
                <FormControl.TimeFormGroup
                  {...FormControl.addProps(`${name}.start_time_recursion_from`, { errors })}
                  value={schedule.start_time_recursion_from}
                  onBlur={onBlur}
                  onChange={this.handleSchedulePropChange.bind(this, "start_time_recursion_from")}
                  label={I18n.t("base.from")}
                  initialValue={schedule.start_time_recursion_from || slot.start_time || undefined}
                  readOnly={readOnlyAccess}
                />

                <FormControl.TimeFormGroup
                  {...FormControl.addProps(`${name}.start_time_recursion_to`, { errors })}
                  value={schedule.start_time_recursion_to}
                  onBlur={onBlur}
                  onChange={this.handleSchedulePropChange.bind(this, "start_time_recursion_to")}
                  label={I18n.t("base.to")}
                  min={slot.start_time_recursion_from}
                  initialValue={
                    schedule.start_time_recursion_to ||
                    schedule.start_time_recursion_from ||
                    slot.start_time ||
                    undefined
                  }
                  readOnly={readOnlyAccess}
                />

                {
                  <FormControl.TimeDurationFormGroup
                    {...FormControl.addProps(`${name}.start_time_recursion_step`, { errors })}
                    value={schedule.start_time_recursion_step}
                    onBlur={onBlur}
                    onChange={this.handleSchedulePropChange.bind(this, "start_time_recursion_step")}
                    label={I18n.t("base.every_each")}
                    durationTypeTimeLengths={[3600, 60]}
                    readOnly={readOnlyAccess}
                  />
                }
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  render() {
    const {
      enable_saas,
      readOnlyAccess,
      product,
      slot,
      name,
      errors,
      loading,
      valid,
      onBlur,
      onChange,
      onClose,
      onRemove,
      removeDisabled,
      onScheduleFormToggle,
      isScheduleBeingEdited,
    } = this.props;
    const { accordionActiveIds, totalStockLimitUpdated } = this.state;
    const selectedProductVariant = product.product_variants.find((pv) => pv.id === slot.product_variant_id);
    selectedProductVariant.availability_type = slot.availability_type;
    const schedule = slot.product_schedule || this.getDefaultSchedule();
    const saleWindowBadge = saleWindowStatus(slot);
    const getTotalStockLimit = () => {
      if (schedule.total_stock_limit !== selectedProductVariant.default_total_stock_limit) {
        return schedule.total_stock_limit;
      } else return selectedProductVariant.default_total_stock_limit;
    };
    return (
      <Form onSubmit={this.handleSubmit.bind(this)} error={!valid} loading={loading}>
        <h3>
          {slot.id
            ? I18n.t("papp.products.EditProductView.StepAvailability.edit_slot")
            : slot._forceStartDateRecurrence
            ? I18n.t("papp.products.EditProductView.StepAvailability.add_many_slots")
            : I18n.t("papp.products.EditProductView.StepAvailability.add_slot")}
        </h3>
        {!!errors.base && <FormMessage error={errors.base} />}
        {product.product_variants.length > 1 && (
          <FormControl.SelectFormGroup
            {...FormControl.addProps(`${name}.product_variant_id`, { errors })}
            value={schedule.product_variant_id}
            onBlur={onBlur}
            onChange={this.handleSlotPropChange.bind(this, "product_variant_id")}
            label={I18n.humanAttributeName("product_schedule_slot", "product_variant")}
            disabled={!!slot.id}
            options={product.product_variants.map((pv) => [pv.uuid, pv.translation.name])}
            inlineLabel
            readOnly={readOnlyAccess}
            className="fluid"
          />
        )}
        <div className="ui field">
          <label>
            {isScheduleBeingEdited
              ? I18n.t("papp.products.EditProductView.StepAvailability.select_first_slot_date")
              : I18n.t("papp.products.EditProductView.StepAvailability.select_slot_date")}
          </label>
          <SlotTimeFormSection
            name={name}
            errors={errors}
            value={slot}
            onBlur={onBlur}
            onChange={onChange}
            readOnly={readOnlyAccess}
          />
        </div>
        <FormControl.CheckboxFormGroup
          {...FormControl.addProps(`${name}.active`, { errors })}
          value={schedule.active}
          onBlur={onBlur}
          onChange={this.handleSlotPropChange.bind(this, "active")}
          label={I18n.humanAttributeName("product_schedule_slot", "active")}
          toggle
          readOnly={readOnlyAccess}
        />
        {enable_saas && (
          <FormControl.CheckboxFormGroup
            {...FormControl.addProps(`${name}.confirmed`, { errors })}
            value={schedule.confirmed}
            onBlur={onBlur}
            onChange={this.handleSlotPropChange.bind(this, "confirmed")}
            label={I18n.humanAttributeName("product_schedule_slot", "confirmed")}
            toggle
            readOnly={readOnlyAccess}
          />
        )}
        {!!slot.start_date && slot._showRecurrenceOutside && this.renderRecurrenceInputs()}
        {!!slot.start_date && (
          <div className="u-marginBottom">
            <Accordion styled fluid className="u-marginVertical">
              <Accordion.Title
                key={`Accordion.Title-${ACCORDION_SCHEDULE}`}
                hidden={slot._hideRecurrenceAccordion}
                active={isScheduleBeingEdited}
                onClick={() => {
                  onScheduleFormToggle(!isScheduleBeingEdited);
                  onBlur();
                }}
              >
                {I18n.t("papp.products.EditProductView.StepAvailability.repeat_slot")} <i className="icon dropdown" />
              </Accordion.Title>
              <Accordion.Content
                key={`Accordion.Content-${ACCORDION_SCHEDULE}`}
                hidden={slot._hideRecurrenceAccordion}
                active={isScheduleBeingEdited}
              >
                {this.renderRecurrenceInputs()}
              </Accordion.Content>

              <Accordion.Title
                key={`Accordion.Title-${ACCORDION_PRICINGS}`}
                hidden={!schedule.active}
                active={accordionActiveIds.includes(ACCORDION_PRICINGS)}
                onClick={this.handleAccordionTitleClick.bind(this, ACCORDION_PRICINGS)}
              >
                {I18n.t("papp.products.EditProductView.StepAvailability.prices_title")} <i className="icon dropdown" />
              </Accordion.Title>

              <Accordion.Content
                key={`Accordion.Content-${ACCORDION_PRICINGS}`}
                hidden={!schedule.active}
                active={accordionActiveIds.includes(ACCORDION_PRICINGS)}
              >
                <div className="ui fields grouped">
                  {selectedProductVariant.product_pricelist_items.map((item) => {
                    const pricing = (schedule.price_pricings || []).find(
                      (pricing) => pricing.product_pricelist_item_id === item.id
                    ) || {
                      uuid: st.generateUuid(),
                      product_pricelist_item_id: item.id,
                      property_name: "price",
                      value_type: "absolute_amount",
                    };
                    return (
                      <FormControl.MoneyFormGroup
                        key={`${name}.price_pricings[product_pricelist_item-${item.uuid}]`}
                        {...FormControl.addProps(`${name}.price_pricings[product_pricelist_item-${item.uuid}]`, {
                          errors,
                        })}
                        value={{
                          value: pricing.absolute_amount_cents !== null ? pricing.absolute_amount_cents / 100 : null,
                          currency: item.price.currency,
                        }}
                        onBlur={onBlur}
                        onChange={this.handlePricingChange.bind(this, pricing)}
                        label={item.display_name}
                        currencyEditable={false}
                        inlineLabel
                        placeholder={item.price}
                        min={0}
                        readOnly={readOnlyAccess}
                      />
                    );
                  })}

                  {selectedProductVariant.product_pricelist_items.length === 0 && (
                    <p>{I18n.t("papp.products.EditProductView.StepAvailability.no_prices_message")}</p>
                  )}
                </div>
              </Accordion.Content>

              <Accordion.Title
                key={`Accordion.Title-${ACCORDION_AVAILABILITY}`}
                hidden={!schedule.active}
                active={accordionActiveIds.includes(ACCORDION_AVAILABILITY)}
                onClick={this.handleAccordionTitleClick.bind(this, ACCORDION_AVAILABILITY)}
              >
                {I18n.t("papp.products.EditProductView.StepAvailability.availability_title")}{" "}
                <i className="icon dropdown" />
              </Accordion.Title>

              <Accordion.Content
                key={`Accordion.Content-${ACCORDION_AVAILABILITY}`}
                hidden={!schedule.active}
                active={accordionActiveIds.includes(ACCORDION_AVAILABILITY)}
              >
                <FormControl.NumberLimitFormGroup
                  {...FormControl.addProps(`${name}.total_stock_limit`, { errors })}
                  value={getTotalStockLimit()}
                  onBlur={onBlur}
                  onChange={this.handleSlotPropChange.bind(this, "total_stock_limit")}
                  label={I18n.humanAttributeName("product_schedule_slot", "total_stock_limit")}
                  inlineLabel
                  min={1}
                  readOnly={readOnlyAccess}
                />

                <FormControl.SelectFormGroup
                  {...FormControl.addProps(`${name}.availability_type`, { errors })}
                  value={
                    selectedProductVariant.availability_type === "available_with_company_approval"
                      ? "available_with_company_approval"
                      : "available_with_instant_booking"
                  }
                  onBlur={onBlur}
                  onChange={this.handleSlotPropChange.bind(this, "availability_type")}
                  label={I18n.t("papp.accommodation.AccommodationSchedulerView.availabilityTypeHeader")}
                  options={[
                    [
                      "available_with_company_approval",
                      I18n.t(
                        "papp.accommodation.AccommodationSchedulerView.availabilityTypes.available_with_company_approval"
                      ),
                    ],
                    [
                      "available_with_instant_booking",
                      I18n.t(
                        "papp.accommodation.AccommodationSchedulerView.availabilityTypes.available_with_instant_booking"
                      ),
                    ],
                  ]}
                  help={
                    slot.availability_type === "available_with_instant_booking"
                      ? I18n.humanAttributeName("product_schedule_slot", "available_with_instant_booking_help")
                      : I18n.humanAttributeName("product_schedule_slot", "available_with_company_approval_help")
                  }
                />
              </Accordion.Content>

              {/* Sale window editor + Biledo metadata. Operator
                  overrides sale_starts_at / sale_ends_at on a per-slot
                  basis. Connectly sync may overwrite on the next cycle
                  for slots linked to Biledo (warning shown below). */}
              <Accordion.Title
                key={`Accordion.Title-${ACCORDION_SALE_WINDOW}`}
                hidden={!schedule.active}
                active={accordionActiveIds.includes(ACCORDION_SALE_WINDOW)}
                onClick={this.handleAccordionTitleClick.bind(this, ACCORDION_SALE_WINDOW)}
              >
                {I18n.t("papp.calendar.ShowSlotModal.sale_window_title")} <i className="icon dropdown" />
              </Accordion.Title>
              <Accordion.Content
                key={`Accordion.Content-${ACCORDION_SALE_WINDOW}`}
                hidden={!schedule.active}
                active={accordionActiveIds.includes(ACCORDION_SALE_WINDOW)}
              >
                {SALE_WINDOW_EDGES.map(({ attribute, edge, labelKey }) => (
                  <FormControl.SaleWindowRuleFormGroup
                    key={attribute}
                    {...FormControl.addProps(`${name}.${attribute}`, { errors })}
                    value={{
                      offset_seconds: slot[`${attribute}_offset_seconds`],
                      at: slot[attribute],
                    }}
                    timeZoneName={slot.time_zone_name}
                    slotStartsAt={slot.starts_at}
                    onBlur={onBlur}
                    onChange={this.handleSaleWindowChange.bind(this, attribute)}
                    label={I18n.t(labelKey)}
                    edge={edge}
                    readOnly={readOnlyAccess || !!slot.connectly_slot}
                  />
                ))}

                {saleWindowBadge && (
                  <div styleName={saleWindowBadge.styleName}>
                    {I18n.t(`papp.calendar.ShowSlotModal.${saleWindowBadge.labelKey}`)}
                  </div>
                )}

                {slot.connectly_slot && (
                  <div styleName="biledo-section">
                    <div styleName="biledo-overwrite-warning">
                      {I18n.t("papp.calendar.ShowSlotModal.biledo_overwrite_warning")}
                    </div>
                    <h5>{I18n.t("papp.calendar.ShowSlotModal.biledo_block_title")}</h5>
                    <dl styleName="biledo-meta">
                      <dt>{I18n.t("papp.calendar.ShowSlotModal.biledo_term_uuid")}</dt>
                      <dd>
                        <code>{slot.connectly_slot.term_uuid}</code>
                      </dd>
                      <dt>{I18n.t("papp.calendar.ShowSlotModal.biledo_pricelist_uuid")}</dt>
                      <dd>
                        <code>{slot.connectly_slot.pricelist_uuid}</code>
                      </dd>
                    </dl>
                  </div>
                )}
              </Accordion.Content>
            </Accordion>

            <FormControl.TextAreaFormGroup
              {...FormControl.addProps(`${name}.note`, { errors })}
              value={schedule.note}
              onBlur={onBlur}
              onChange={this.handleSlotPropChange.bind(this, "note")}
              placeholder={I18n.humanAttributeName("product_schedule_slot", "note")}
              autoHeight
              readOnly={readOnlyAccess}
            />
          </div>
        )}
        <Button
          as="a"
          basic
          disabled={loading}
          onClick={onClose}
          content={readOnlyAccess ? I18n.t("papp.common.close") : I18n.t("papp.common.cancel")}
        />
        &nbsp;
        {!readOnlyAccess && onRemove && (
          <span onClick={onRemove}>
            <Button
              as="a"
              basic
              color="red"
              disabled={loading || removeDisabled}
              content={I18n.t("papp.common.remove")}
            />
          </span>
        )}
        {!readOnlyAccess && (
          <Button type="submit" primary floated="right" disabled={loading} content={I18n.t("papp.common.save")} />
        )}
      </Form>
    );
  }
}



// WEBPACK FOOTER //
// ./src/modules/products/EditProductView/StepAvailability/ProductScheduleSlotForm.jsx